/**
 * Straight-line distance between two points, in kilometres.
 *
 * **A deliberate port of `backend/src/utils/Geo.js`, line for line.** The server prices an
 * order from its own measurement of this distance. If the browser computed even a metre
 * differently, the fee on the summary and the fee on the placed order would disagree —
 * and the customer is the one who notices. The arithmetic here is not *equivalent*, it is
 * the same, including the radius and the rounder.
 *
 * Straight-line, not road distance. Road distance means the Directions API, which is not
 * free, and the server runs this on every placement and every dispatch. The consequence is
 * stated rather than hidden: a delivery across a river with a bridge three kilometres
 * upstream is priced as if the rider could swim. In Laguna, over a few kilometres of
 * connected streets, the error is small.
 *
 * Rounded to metres, for the same reason `fare.js` rounds distance to three places: two
 * would make anything under ten metres free, and float noise otherwise leaks into a fare.
 */

const EARTH_RADIUS_KM = 6371

const toRadians = (degrees) => (degrees * Math.PI) / 180

/**
 * One coordinate, or `null` — and `null` for the things `Number()` would quietly accept.
 *
 * `Number(null)` is 0, `Number('')` is 0, and `Number([])` is 0. A finite zero is a real
 * longitude, so a point missing half of itself used to measure from the prime meridian:
 * `{ latitude: 14, longitude: null }` came back 12,830 km from Los Baños rather than
 * refusing. That distance then prices as refused, or worse, as a very expensive delivery.
 *
 * Only a number or a numeric string is a coordinate. Everything else is an unanswered
 * question.
 */
const coordinate = (value) => {
  if (value === null || value === undefined || value === '') return null

  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : null
}

/**
 * @param {{latitude: number, longitude: number}} from
 * @param {{latitude: number, longitude: number}} to
 * @returns {number|null} — `null` when either point is not a usable pair, which is an
 *   unanswered question rather than a delivery of no distance.
 */
export function distanceKm(from, to) {
  const lat1 = coordinate(from?.latitude)
  const lon1 = coordinate(from?.longitude)
  const lat2 = coordinate(to?.latitude)
  const lon2 = coordinate(to?.longitude)

  if ([lat1, lon1, lat2, lon2].some((value) => value === null)) {
    return null
  }

  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)

  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return Math.round(EARTH_RADIUS_KM * c * 1000) / 1000
}

/** The form's own location shape into the one this measures. */
export const asPoint = (location) =>
  (location?.lat == null || location?.lng == null
    ? null
    : { latitude: location.lat, longitude: location.lng })

export { EARTH_RADIUS_KM }
